import { Button } from 'antd';
import * as Icon from 'react-feather';
import { Table } from '@tanstack/react-table';
import {
  ActionButton,
  SampleExcelUrl,
  ApiErrorResponse,
  ApiError,
  GetApiErrorMessage,
  getExcelErrorMessage,
  useSampleEditSessionHasDraftsQuery,
  SampleDataSubjectFormDataType,
  SampleDataSubjectValidFormDataType,
  SampleValidFormDataType,
  confirmPanelFormClose,
  useNavigationBlockers,
  useEditSessionContext,
  useEditSessionPending,
  useEditSessionFormRowsContext,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { OpenDirectoryButton } from '../../common/OpenDirectoryButton';
import { path } from '../../paths';
import { useNavigateTo } from '../../paths/useNavigateTo';
import { useSampleContext } from './state/SampleContext';
import translations from './SampleActions.translations.json';
import { LockState } from './state/SampleContext.types';
import { RefObject, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { expand } from 'uri-template-lite';
import { SampleUpload } from './SampleUpload';
import { SampleDataObjectType } from './SampleSearch';
import { useSampleDetailsContext } from './state/SampleDetailsContext';
import { useHasPermissionToManageSample } from './useHasPermissionToManageSample';

const { addTranslations, useTranslation } = i18nNsFactory('sample-actions');
addTranslations(translations);

export interface SampleActionsProps {
  id: number;
  onError?: (
    apiErrors: ApiError[],
    getErrorMessage?: GetApiErrorMessage,
  ) => void;
  valueDate: string;
  sampleType: SampleDataObjectType;
  tableRef: RefObject<Table<SampleDataSubjectFormDataType> | null>;
}

export const SampleActions = ({
  id,
  onError,
  valueDate,
  sampleType,
  tableRef,
}: SampleActionsProps) => {
  const navigateToDataSubjectDirectory = useNavigateTo(path.sampleDirectory);
  const { t } = useTranslation();
  const { selectedGroupMemberId } = useSampleContext();
  const { clear: clearBlockers } = useNavigationBlockers();
  const { viewType, setViewType, isEditing } = useSampleDetailsContext();
  const { getValues, setValue, reset } =
    useFormContext<SampleValidFormDataType>();
  const { sessionId, discardEdits, insertRows } = useEditSessionContext();
  const isPending = useEditSessionPending();
  const { unvalidatedRowIndex } = useEditSessionFormRowsContext();
  const [isUploadPending, setIsUploadPending] = useState(false);
  const isActionPending = isPending || isUploadPending;

  const handleLocking = useCallback(
    (state: LockState) => {
      const isLocked = state === 'lock';
      const opts = { shouldDirty: true };

      const displayedRows = tableRef.current?.getFilteredRowModel().rows;

      displayedRows?.forEach((row) => {
        if (!selectedGroupMemberId) {
          setValue(`samples.${row.index}.isLocked`, isLocked, opts);
          return;
        }

        const name = `samples.${row.index}.sampleDataObjects` as const;
        const existingSampleDataObjects = getValues(name);
        const existingSampleDataObject = existingSampleDataObjects?.find(
          (o) => o.dataObjectDescriptionId === selectedGroupMemberId,
        );

        if (existingSampleDataObject) {
          setValue(
            name,
            existingSampleDataObjects.map((o) =>
              o === existingSampleDataObject ? { ...o, isLocked } : o,
            ),
            opts,
          );
        } else if (isLocked) {
          setValue(
            name,
            [
              ...(existingSampleDataObjects ?? []),
              {
                dataObjectDescriptionId: selectedGroupMemberId,
                isLocked,
                hasActive: false,
                comments: '',
              },
            ],
            opts,
          );
        }
      });
    },
    [tableRef, selectedGroupMemberId, getValues, setValue],
  );

  const { refetch: fetchHasDrafts } = useSampleEditSessionHasDraftsQuery(
    { sessionId: sessionId ?? '0' },
    { enabled: false },
  );

  const handleAbort = useCallback(async () => {
    const onClose = () => {
      if (!isEditing) {
        navigateToDataSubjectDirectory();
      } else {
        reset(undefined, {
          keepDirty: false,
        });
        clearBlockers();
        void discardEdits();
      }
    };

    if (!isEditing || !sessionId) {
      onClose();
      return;
    }

    const { data } = await fetchHasDrafts();
    if (isPending || data?.hasAnyPendingDrafts) {
      confirmPanelFormClose(onClose);
    } else {
      onClose();
    }
  }, [
    isEditing,
    sessionId,
    fetchHasDrafts,
    isPending,
    navigateToDataSubjectDirectory,
    reset,
    clearBlockers,
    discardEdits,
  ]);

  const handleUpload = useCallback(
    async (uploaded: SampleDataSubjectFormDataType[]) => {
      const samples = getValues(
        'samples',
      ) as SampleDataSubjectValidFormDataType[];
      const newSamples = filterNewSampleUploads(uploaded, samples);
      await insertRows(newSamples);
    },
    [getValues, insertRows],
  );

  const handleError = useCallback(
    (error: ApiErrorResponse) => {
      onError?.(error.response?.data.errors ?? [], getExcelErrorMessage);
    },
    [onError],
  );

  const hasPermissionToManageSample = useHasPermissionToManageSample(
    id,
    sampleType,
  );

  const urlParams = { id, valueDate, type: sampleType };

  return (
    <>
      {isEditing && hasPermissionToManageSample && (
        <>
          <ActionButton
            onClick={() => handleLocking('lock')}
            type="abort"
            layoutType={viewType}
          >
            {t('button.lockDisplayed')}
          </ActionButton>
          <ActionButton
            onClick={() => handleLocking('unlock')}
            type="abort"
            layoutType={viewType}
          >
            {t('button.unlockDisplayed')}
          </ActionButton>
          <SampleUpload
            {...urlParams}
            onError={handleError}
            onPendingChange={setIsUploadPending}
            onSuccess={handleUpload}
          >
            <Button
              id="upload-samples"
              disabled={isActionPending}
              loading={isUploadPending}
              style={{ display: 'grid', placeItems: 'center' }}
              icon={<Icon.Upload size={18} />}
            />
          </SampleUpload>
        </>
      )}
      <Button
        id="download-samples"
        style={{ display: 'grid', placeItems: 'center' }}
        icon={<Icon.Download size={18} />}
        href={expand(SampleExcelUrl, urlParams)}
      />
      {isEditing ? (
        <ActionButton
          type="abort"
          onClick={handleAbort}
          disabled={isActionPending}
        />
      ) : (
        <OpenDirectoryButton
          options={[
            {
              id: 'inputDataDirectory',
              path: path.inputDataDirectory,
              label: t('button.inputDataDirectory'),
            },
            {
              id: 'inputDataErrorDirectory',
              path: path.inputDataErrorDirectory,
              label: t('button.inputDataErrorDirectory'),
            },
          ]}
        />
      )}
      {hasPermissionToManageSample &&
        (isEditing ? (
          <ActionButton
            loading={isActionPending}
            disabled={
              isActionPending || !sessionId || unvalidatedRowIndex !== null
            }
            type="formSubmit"
            layoutType="mutate"
          />
        ) : (
          <ActionButton
            onClick={() => setViewType('mutate')}
            type="mainAction"
            layoutType="detail"
          />
        ))}
    </>
  );
};

interface SampleWithSubjectId {
  dataSubject: { id: number };
}

const filterNewSampleUploads = <Uploaded extends SampleWithSubjectId>(
  uploaded: Uploaded[],
  existing: SampleWithSubjectId[],
) => {
  const knownSubjectIds = new Set(
    existing.map(({ dataSubject }) => dataSubject.id),
  );

  return uploaded.filter(({ dataSubject }) => {
    const id = dataSubject.id;
    if (!id || knownSubjectIds.has(id)) {
      return false;
    }
    knownSubjectIds.add(id);
    return true;
  });
};
